import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Package, ShieldCheck, Globe, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { products, trustIndicators } from '../data/mock';

const Home = () => {
  const featuredProducts = products.filter((p) => p.featured).slice(0, 6);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1650804068570-7fb2e3dbf888?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxibGFjayUyMG1hcmJsZXxlbnwwfHx8fDE3NjkxMDg2NTl8MA&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}>

          <div className="absolute inset-0 bg-black/50"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center text-white">
          <h1 className="md:text-7xl !font-bold !text-5xl mb-6">
            Rock Granites<br />for Global Projects
          </h1>
          <p className="md:text-2xl !font-light !text-xl mb-6 text-gray-200">Export-quality granite, sourced from India's finest quarries</p>

          {/* Star Export House Badge */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex items-center gap-2 border border-amber-400 rounded-full px-5 py-2 bg-amber-400/10 backdrop-blur-sm">
              <span className="text-amber-400 text-lg">★</span>
              <div className="text-left">
                <div className="text-amber-300 text-sm font-bold tracking-widest uppercase">A Star Export House</div>
                <div className="text-amber-400/80 text-xs tracking-wide">DGFT · Government of India Recognised</div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[#111111] hover:bg-gray-100 text-lg px-8 py-6">
                Request a Quote <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-16 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {trustIndicators.map((indicator, index) =>
            <div key={index} className="text-center">
                <div className="flex justify-center mb-4">
                  {index === 0 && <Package size={40} className="text-[#111111]" />}
                  {index === 1 && <ShieldCheck size={40} className="text-[#111111]" />}
                  {index === 2 && <Globe size={40} className="text-[#111111]" />}
                  {index === 3 && <Award size={40} className="text-[#111111]" />}
                </div>
                <h3 className="text-lg font-semibold mb-2 text-[#111111]">{indicator.title}</h3>
                <p className="text-gray-600 text-sm">{indicator.description}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#111111] mb-4">
              Our Premium Collection
            </h2>
            <p className="max-w-2xl !text-lg mx-auto text-gray-600">Explore Variety That’s Reliable

            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {featuredProducts.map((product) =>
            <Link key={product.id} to={`/products/${product.slug}`}>
                <Card className="group overflow-hidden border-0 shadow-sm hover:shadow-xl transition-all duration-300">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                    src={product.images[0]}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />

                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold mb-2 text-[#111111]">{product.name}</h3>
                    <p className="text-gray-600 text-sm mb-4 leading-relaxed">{product.description}</p>
                    <div className="flex items-center text-sm text-gray-500">
                      <span>Origin: {product.origin}</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )}
          </div>

          <div className="text-center mt-12">
            <Link to="/products">
              <Button size="lg" className="bg-[#111111] text-white hover:bg-[#2a2a2a]">
                View All Products <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Rock Granites */}
      <section className="py-24 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#111111] mb-6">
                Why Rock Granites?
              </h2>
              <p className="!text-lg mb-8 text-gray-600">With 50+ years of experience in granite export, we understand the demands of international markets. Our commitment to quality control, consistent grading, and reliable logistics makes us the preferred partner for importers, architects, and developers worldwide.

              </p>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-[#111111] flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#111111] mb-1">Direct Quarry Sourcing</h4>
                    <p className="text-gray-600 text-sm">We work directly with quarry owners ensuring authenticity and best pricing</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-[#111111] flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#111111] mb-1">Rigorous Quality Control</h4>
                    <p className="text-gray-600 text-sm">Multi-stage inspection ensures consistent grading across all shipments</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-[#111111] flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#111111] mb-1">Export Expertise</h4>
                    <p className="text-gray-600 text-sm">Complete documentation, packaging, and logistics support for seamless delivery</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="relative h-[600px]">
              <img
                src="https://customer-assets.emergentagent.com/job_ad7e8f87-1d9a-44c7-94bb-e1bf1711e4b9/artifacts/oip7r9t8_hf_20260124_142031_30780690-1513-45db-9a61-f52216b1e259%20%281%29.png"
                alt="Export Logistics"
                className="absolute inset-0 w-full h-full object-cover rounded-lg shadow-2xl" />

            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#111111] text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="!text-xl mb-12 text-gray-300">Get a quote within 24-48 hours. 

          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-[#111111] hover:bg-gray-100 text-lg px-8 py-6">
              Request Quote <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>);

};

export default Home;